/**
 * @file schemas/password.ts
 * @description 登入密碼規則（2026-09-25 客戶回報序50/51，前後端共用）。
 *
 * 規則：
 * - 使用者自行設定的密碼（修改密碼 / 忘記密碼重設）：6–10 個字元，僅限英文字母與數字，
 *   且須「同時包含」英文與數字；英文不限大小寫。
 * - 系統預設初始密碼 `1234567890`（純數字）：屬管理員發放／「恢復初始密碼」的例外，
 *   僅在管理員建立帳號、重設密碼時允許；使用者登入後會被提示盡速修改。
 *
 * 前端 antd 表單以 `validatePasswordPolicy` 做即時校驗，後端以 zod schema 做最終把關，
 * 兩者共用同一套常量，避免規則漂移。
 */
import { z } from 'zod';
/** 密碼最短長度 */
export const PASSWORD_MIN_LENGTH = 6;
/** 密碼最長長度 */
export const PASSWORD_MAX_LENGTH = 10;
/** 系統預設初始密碼（員工帳號建立 / 恢復初始密碼時使用） */
export const DEFAULT_INITIAL_PASSWORD = '1234567890';
/** 密碼規則提示文案（表單 placeholder / 說明共用） */
export const PASSWORD_RULE_HINT = '6–10 個字元，須同時包含英文與數字（不限大小寫）';
/**
 * 校驗密碼是否符合使用者自訂密碼規則。
 *
 * @param password 待校驗的密碼
 * @returns 不符合時回傳中文錯誤訊息；符合時回傳 null
 */
export function validatePasswordPolicy(password) {
    if (password.length < PASSWORD_MIN_LENGTH || password.length > PASSWORD_MAX_LENGTH) {
        return `密碼長度須為 ${PASSWORD_MIN_LENGTH}–${PASSWORD_MAX_LENGTH} 個字元`;
    }
    if (!/^[A-Za-z0-9]+$/.test(password)) {
        return '密碼僅能使用英文字母與數字';
    }
    if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) {
        return '密碼須同時包含英文字母與數字';
    }
    return null;
}
/**
 * 使用者自訂密碼 schema（修改密碼、忘記密碼重設）。
 * 錯誤訊息直接取自 `validatePasswordPolicy`，前後端提示一致。
 */
export const passwordPolicySchema = z.string().superRefine((value, ctx) => {
    const error = validatePasswordPolicy(value);
    if (error)
        ctx.addIssue({ code: 'custom', message: error });
});
/**
 * 管理員設定密碼 schema（建立員工帳號的初始密碼、重設密碼）。
 * 除符合一般規則外，額外允許系統預設初始密碼 `1234567890`。
 */
export const adminAssignedPasswordSchema = z.string().superRefine((value, ctx) => {
    if (value === DEFAULT_INITIAL_PASSWORD)
        return;
    const error = validatePasswordPolicy(value);
    if (error)
        ctx.addIssue({ code: 'custom', message: error });
});
/**
 * 管理員重設員工密碼請求 schema（POST /api/settings/staff/:id/reset-password）。
 * - `restoreInitial: true` → 恢復為系統預設初始密碼（忽略 newPassword）
 * - 否則必填 `newPassword`（符合一般規則，或等於預設初始密碼）
 */
export const staffResetPasswordSchema = z
    .object({
    newPassword: adminAssignedPasswordSchema.optional(),
    restoreInitial: z.boolean().optional(),
})
    .refine((v) => v.restoreInitial === true || typeof v.newPassword === 'string', {
    message: '請輸入新密碼或選擇恢復初始密碼',
    path: ['newPassword'],
});
/**
 * 解析重設密碼請求最終要寫入的明文密碼。
 *
 * @param input 已通過 staffResetPasswordSchema 校驗的請求
 * @returns 要寫入（雜湊前）的密碼
 */
export function resolveStaffResetPassword(input) {
    if (input.restoreInitial)
        return DEFAULT_INITIAL_PASSWORD;
    return input.newPassword ?? DEFAULT_INITIAL_PASSWORD;
}
